import React, { useState } from 'react';
import { Nav, Dropdown } from 'react-bootstrap';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'dark');

  const toggleTheme = (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.body.setAttribute('data-theme', newTheme);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActiveRoute = (path) => {
    return location.pathname === path;
  };

  const navigationItems = [
    {
      path: '/machines',
      icon: 'fas fa-desktop',
      label: 'Machines',
      description: 'Monitor & control hosts'
    },
    {
      path: '/clusters',
      icon: 'fas fa-layer-group',
      label: 'Clusters',
      description: 'Bulk operations'
    },
    {
      path: '/logs',
      icon: 'fas fa-history',
      label: 'Audit Logs',
      description: 'System logging'
    }
  ];

  if (user?.role === 'admin') {
    navigationItems.push({
      path: '/users',
      icon: 'fas fa-users-cog',
      label: 'User Accounts',
      description: 'Role-based access'
    });
  }

  return (
    <div 
      className="sidebar-container d-flex flex-column h-100"
      style={{
        width: '280px',
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
        zIndex: 1000,
      }}
    >
      {/* Logo Section */}
      <div className="p-4" style={{ borderBottom: '1px solid var(--border-color)' }}>
        <Link 
          to="/" 
          className="d-flex align-items-center text-decoration-none"
        >
          <div 
            className="d-flex align-items-center justify-content-center me-3"
            style={{
              width: '40px',
              height: '40px',
              backgroundColor: 'var(--color-primary)',
              borderRadius: '8px',
              color: 'white',
              fontSize: '1.1rem'
            }}
          >
            <i className="fas fa-network-wired"></i>
          </div>
          <div>
            <h4 className="mb-0 text-theme-main" style={{ fontWeight: '800', fontSize: '1.25rem', letterSpacing: '-0.02em' }}>
              LANtern
            </h4>
            <small className="text-theme-muted" style={{ fontSize: '0.7rem', fontWeight: 700 }}>
              NETWORK COMMANDS
            </small>
          </div>
        </Link>
      </div>

      {/* Navigation Menu */}
      <div className="flex-grow-1 p-3">
        <div className="mb-4">
          <h6 className="text-theme-muted text-uppercase mb-3 px-2" style={{ 
            fontSize: '0.65rem', 
            fontWeight: '700',
            letterSpacing: '1.5px'
          }}>
            Manage
          </h6>
          <Nav className="flex-column">
            {navigationItems.map((item) => (
              <Nav.Link
                key={item.path}
                as={Link}
                to={item.path}
                className="d-flex align-items-center p-3 mb-2 rounded-lg text-decoration-none"
                style={{
                  color: isActiveRoute(item.path) ? 'var(--text-highlight)' : 'var(--text-muted)',
                  backgroundColor: isActiveRoute(item.path) ? 'var(--bg-card-hover)' : 'transparent',
                  borderLeft: isActiveRoute(item.path) ? '3px solid var(--color-primary)' : '3px solid transparent',
                  paddingLeft: isActiveRoute(item.path) ? '13px' : '16px',
                  transition: 'all 0.15s ease',
                  fontSize: '0.9rem'
                }}
              >
                <i 
                  className={`${item.icon} me-3`} 
                  style={{ 
                    fontSize: '1rem',
                    width: '20px',
                    textAlign: 'center',
                    color: isActiveRoute(item.path) ? 'var(--color-primary)' : 'var(--text-muted)'
                  }}
                ></i>
                <div className="d-flex flex-column">
                  <span style={{ fontWeight: isActiveRoute(item.path) ? '700' : '500', color: 'var(--text-main)' }}>
                    {item.label}
                  </span>
                  <span className="text-theme-muted" style={{ fontSize: '0.7rem', marginTop: '1px' }}>
                    {item.description}
                  </span>
                </div>
              </Nav.Link>
            ))}
          </Nav>
        </div>
      </div>

      {/* Theme Switcher */}
      <div className="px-4 mb-2">
        <div className="theme-switch-container">
          <span className="text-theme-muted" style={{ fontSize: '0.7rem', fontWeight: '700', letterSpacing: '0.5px' }}>THEME MODE</span>
          <div className="d-flex gap-1">
            <button 
              className={`theme-toggle-btn ${theme === 'light' ? 'active' : ''}`}
              onClick={() => toggleTheme('light')}
              title="Light Mode"
            >
              <i className="fas fa-sun"></i>
            </button>
            <button 
              className={`theme-toggle-btn ${theme === 'dark' ? 'active' : ''}`}
              onClick={() => toggleTheme('dark')}
              title="Dark Mode"
            >
              <i className="fas fa-moon"></i>
            </button>
          </div>
        </div>
      </div>

      {/* User Session Info */}
      <div style={{ borderTop: '1px solid var(--border-color)' }}>
        <Dropdown dropup className="w-100">
          <Dropdown.Toggle 
            variant="link"
            className="d-flex align-items-center w-100 p-4 text-decoration-none border-0"
            style={{
              backgroundColor: 'transparent',
              color: 'var(--text-main)',
              fontSize: '0.9rem'
            }}
          >
            <div 
              className="d-flex align-items-center justify-content-center me-3"
              style={{
                width: '36px',
                height: '36px',
                backgroundColor: 'var(--bg-main)',
                border: '1px solid var(--border-color)',
                borderRadius: '50%',
                color: 'var(--text-muted)',
                fontSize: '0.9rem'
              }}
            >
              <i className="fas fa-user-shield"></i>
            </div>
            <div className="flex-grow-1 text-start">
              <div className="text-theme-main" style={{ fontSize: '0.85rem', fontWeight: '700' }}>
                {user?.username || 'Admin'}
              </div>
              <div className="text-theme-muted text-uppercase" style={{ fontSize: '0.65rem', fontWeight: '700', letterSpacing: '0.5px' }}>
                {user?.role || 'user'}
              </div>
            </div>
            <i className="fas fa-chevron-up ms-2 text-theme-muted" style={{ fontSize: '0.75rem' }}></i>
          </Dropdown.Toggle>

          <Dropdown.Menu 
            className="w-100 border-0"
            style={{
              backgroundColor: 'var(--dropdown-bg)',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              padding: '0.35rem',
              marginBottom: '10px'
            }}
          >
            <Dropdown.Item 
              onClick={handleLogout} 
              className="d-flex align-items-center rounded-md text-danger py-2"
              style={{
                fontSize: '0.85rem',
                fontWeight: 600
              }}
            >
              <i className="fas fa-sign-out-alt me-2"></i>
              Disconnect Session
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>
    </div>
  );
}

export default Sidebar;